// Presentation guard for mission maps. Never grants commercial access;
// PlayAuthorization and the backend remain authoritative when gameplay begins.
import { createMissionContentAccess } from './MissionContentAccess';
import { button, element } from '../util/menuUI';

export const PREMIUM_ADULT_PATH = '/account';

export function createPremiumMissionMap(scene, root) {
  const access = createMissionContentAccess(scene, render);
  const commercial = access.commercial;
  const cards = [];
  let active = true;
  let dialog = null;
  let lastTrigger = null;

  const cleanup = () => {
    active = false;
    dialog?.remove();
    dialog = null;
    scene.events.off('shutdown', cleanup);
    scene.events.off('destroy', cleanup);
  };
  scene.events.once('shutdown', cleanup);
  scene.events.once('destroy', cleanup);

  function render() {
    if (!active) return;
    const state = access.getState();
    for (const { card, index, academic, label } of cards) {
      if (!commercial || index === 0) {
        card.disabled = !academic;
        continue;
      }
      const granted = state === 'premium';
      const checking = state === 'loading';
      card.classList.toggle('tmag-premium-locked', !granted && !checking);
      card.classList.toggle('tmag-premium-checking', checking);
      card.disabled = checking || (granted && !academic);
      if (!granted) {
        label.textContent = checking ? '⌛ VERIFICANDO ACCESO' :
          state === 'error' ? '⚠ VERIFICAR ACCESO' : '🔒 PREMIUM';
      }
      else label.textContent = label.dataset.academicLabel;
    }
  }
  function openDialog(reason, trigger) {
    if (!active || dialog) return;
    lastTrigger = trigger;
    const overlay = element('div', 'tmag-premium-dialog-overlay');
    const panel = element('div', 'tmag-premium-dialog');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', 'Acceso a misiones Premium');
    const title = element('h2', '', reason === 'error' ? 'Verifica tu acceso' : '🔒 Misión Premium');
    const desc = element('p', '', reason === 'error'
      ? 'No pudimos comprobar tu suscripción. Tu progreso está a salvo.'
      : '¡Tu aventura continúa! La primera misión de cada mundo es gratis. Para explorar los demás planetas, pide ayuda a un adulto.');
    panel.append(title, desc);
    if (reason !== 'error') {
      panel.append(element('p', 'tmag-premium-dialog-price', 'Premium se activa por estudiante. Un adulto administra la cuenta y el pago.'));
    }
    const actions = element('div', 'tmag-premium-dialog-actions');
    let focusTarget;
    if (reason === 'error') {
      focusTarget = button('Volver a comprobar', () => {
        close(); access.refresh();
      }, 'tmag-button tmag-premium-dialog-main');
      actions.append(focusTarget);
    } else {
      const adult = element('a', 'tmag-button tmag-premium-dialog-main', '👨‍👩‍👧 Crear o entrar a la cuenta familiar');
      adult.href = new URL(PREMIUM_ADULT_PATH, window.location.origin).href;
      adult.target = '_blank';
      adult.rel = 'noopener noreferrer';
      adult.setAttribute('aria-label', 'Abrir la cuenta familiar para un adulto en otra pestaña');
      focusTarget = adult;
      actions.append(adult);
      const whatsapp = element('a', 'tmag-button tmag-premium-dialog-share', 'Enviar enlace al adulto por WhatsApp');
      whatsapp.href = 'https://wa.me/?text=' + encodeURIComponent('Para crear tu cuenta familiar y conocer Premium de TechnoMathPR: ' + adult.href);
      whatsapp.target = '_blank';
      whatsapp.rel = 'noopener noreferrer';
      whatsapp.setAttribute('aria-label', 'Compartir con un adulto el enlace de la cuenta TechnoMathPR por WhatsApp');
      actions.append(whatsapp);
      const share = button('Copiar enlace para un adulto', async () => {
        try {
          await navigator.clipboard.writeText(adult.href);
          share.textContent = '✓ Enlace copiado';
        } catch {
          const address = element('input', 'tmag-premium-dialog-link');
          address.readOnly = true;
          address.value = adult.href;
          address.setAttribute('aria-label', 'Enlace para compartir con un adulto');
          panel.append(address);
          address.focus(); address.select();
        }
      }, 'tmag-button tmag-premium-dialog-share');
      actions.append(share);
    }
    const cancel = button('Seguir jugando gratis', close, 'tmag-button tmag-premium-dialog-dismiss');
    actions.append(cancel);
    panel.append(actions);
    overlay.append(panel);
    root.append(overlay);
    dialog = overlay;
    function close() {
      overlay.remove();
      dialog = null;
      lastTrigger?.focus({ preventScroll: true });
    }
    panel.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key !== 'Tab') return;
      const items = [...panel.querySelectorAll('a[href],button:not([disabled]),input')];
      const first = items[0], last = items[items.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    focusTarget.focus({ preventScroll: true });
  }

  return {
    register(card, index, academic, label) {
      // Preserve the academic lock label when Premium is active.
      label.dataset.academicLabel = label.textContent;
      cards.push({ card, index, academic, label });
      render();
    },
    guard(card, index) {
      if (access.allows(index)) return false;
      const state = access.getState();
      if (state === 'loading') return true;
      openDialog(state === 'error' ? 'error' : 'premium', card);
      return true;
    },
  };
}
